import { useMemo, useState } from 'react'
import { EventReminderConfig, ReminderSchedule } from '../../lib/db/db.types'

interface EventRemindersProps {
  reminders?: EventReminderConfig
  onUpdate: (reminders: EventReminderConfig) => void
  users: Array<{ id: string; email: string; name?: string }>
  eventDate: string
}

type TimeUnit = 'months' | 'weeks' | 'days'

export const EventReminders = ({
  reminders,
  onUpdate,
  users,
  eventDate,
}: EventRemindersProps) => {
  const [schedules, setSchedules] = useState<ReminderSchedule[]>(
    reminders?.schedules ?? [],
  )
  const [recipients, setRecipients] = useState<string[]>(
    reminders?.recipients ?? [],
  )

  const compareSchedules = (
    a: ReminderSchedule,
    b: ReminderSchedule,
  ): number => {
    const getDays = (schedule: ReminderSchedule) => {
      const { months, weeks, days } = schedule.beforeDate
      return (months || 0) * 30 + (weeks || 0) * 7 + (days || 0)
    }
    return getDays(b) - getDays(a)
  }

  const addSchedule = () => {
    const newSchedule: ReminderSchedule = {
      beforeDate: { days: 1 },
    }
    setSchedules([...schedules, newSchedule])
  }

  const removeSchedule = (index: number) => {
    const newSchedules = schedules.filter((_, i) => i !== index)
    setSchedules(newSchedules)
  }

  const updateSchedule = (index: number, value: number, unit: TimeUnit) => {
    const newSchedules = [...schedules]
    newSchedules[index] = {
      beforeDate: { [unit]: value },
    }
    setSchedules(newSchedules)
  }

  const getScheduleValue = (
    schedule: ReminderSchedule,
  ): { value: number; unit: TimeUnit } => {
    if (schedule.beforeDate.months)
      return { value: schedule.beforeDate.months, unit: 'months' }
    if (schedule.beforeDate.weeks)
      return { value: schedule.beforeDate.weeks, unit: 'weeks' }
    return { value: schedule.beforeDate.days || 0, unit: 'days' }
  }

  const getScheduleDescription = (schedule: ReminderSchedule): string => {
    const { months, weeks, days } = schedule.beforeDate
    const reminderDate = new Date(eventDate)

    if (months) reminderDate.setMonth(reminderDate.getMonth() - months)
    if (weeks) reminderDate.setDate(reminderDate.getDate() - weeks * 7)
    if (days) reminderDate.setDate(reminderDate.getDate() - days)

    const formattedDate = reminderDate.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    return formattedDate
  }

  const hasChanges = useMemo((): boolean => {
    const originalSchedules = reminders?.schedules ?? []
    const originalRecipients = reminders?.recipients ?? []

    const schedulesStringified = schedules
      .map((s) =>
        Object.entries(s.beforeDate)
          .map(([key, value]) => `${key}:${value}`)
          .sort()
          .join(','),
      )
      .sort()
      .join('|')

    const originalSchedulesStringified = originalSchedules
      .map((s) =>
        Object.entries(s.beforeDate)
          .map(([key, value]) => `${key}:${value}`)
          .sort()
          .join(','),
      )
      .sort()
      .join('|')

    if (schedulesStringified !== originalSchedulesStringified) return true

    // Compare recipients
    return (
      !recipients.every((r) => originalRecipients.includes(r)) ||
      !originalRecipients.every((r) => recipients.includes(r))
    )
  }, [schedules, recipients, reminders])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Event Reminders</h2>
        <p className="text-gray-600 mt-1">
          Configure when and who should receive reminders for this event.
        </p>
      </div>

      <div className="bg-white p-4 rounded-md shadow-sm space-y-3">
        <h3 className="font-medium text-gray-700">When to Send Reminders</h3>
        {schedules.map((schedule, index) => {
          const { value, unit } = getScheduleValue(schedule)
          return (
            <div
              key={index}
              className="flex items-center gap-3 p-2 bg-gray-50 rounded-md"
            >
              <input
                type="number"
                min="1"
                max={unit === 'months' ? 12 : unit === 'weeks' ? 52 : 31}
                value={value}
                onChange={(e) =>
                  updateSchedule(index, parseInt(e.target.value), unit)
                }
                className="w-20 border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <select
                value={unit}
                onChange={(e) =>
                  updateSchedule(index, value, e.target.value as TimeUnit)
                }
                className="border rounded-md px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="months">months</option>
                <option value="weeks">weeks</option>
                <option value="days">days</option>
              </select>
              <span className="text-gray-600">before the event</span>
              <span className="text-sm text-gray-500 ml-2">
                {getScheduleDescription(schedule)}
              </span>
              <button
                onClick={() => removeSchedule(index)}
                className="ml-auto text-gray-400 hover:text-red-600 transition-colors"
                title="Remove reminder"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )
        })}
        <button
          onClick={addSchedule}
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Another Reminder
        </button>
      </div>

      {users.length > 0 && (
        <div className="bg-white p-4 rounded-md shadow-sm space-y-3">
          <h3 className="font-medium text-gray-700">
            Who Should Receive Reminders
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {users.map((user) => (
              <label
                key={user.id}
                className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-md"
              >
                <input
                  type="checkbox"
                  checked={recipients.includes(user.id)}
                  onChange={(e) => {
                    const newRecipients = e.target.checked
                      ? [...recipients, user.id]
                      : recipients.filter((id) => id !== user.id)
                    setRecipients(newRecipients)
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-gray-700">
                  {user.name ? user.name : user.email}
                  {!!user.name && (
                    <span className="text-gray-500 text-sm ml-1">
                      ({user.email})
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {hasChanges && (
        <>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setSchedules(reminders?.schedules ?? [])
                setRecipients(reminders?.recipients ?? [])
              }}
              className="w-full py-2.5 px-4 rounded-md font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const sortedSchedules = [...schedules].sort(compareSchedules)
                onUpdate({
                  schedules: sortedSchedules,
                  recipients,
                })
              }}
              className="w-full py-2.5 px-4 rounded-md font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white"
            >
              Save Reminder Settings
            </button>
          </div>
        </>
      )}
    </div>
  )
}
